import {call, put, takeLatest} from 'redux-saga/effects'
import {toast} from 'react-toastify'
import {
  getUserProfile,
  setUserProfile,
  updateUserProfile,
  redeemReferralCode,
  redeemReferralCodeSuccess,
  showError,
  setShowProfileEditor,
  changePassword,
  setShowPasswordChange,
  getSubscriptionPlans,
  setSubscriptionPlans,
  changePasswordSuccess,
  verifyUser,
  updateUserInvoice,
  setShowInvoiceEditor,
  setBankTransferRequestModal,
  updateUserWatermarkInfo,
} from './ProfileRedux'
import {
  getUserProfileRequest,
  updateUserProfileRequest,
  redeemReferralCodeRequest,
  changePasswordRequest,
  getSubscriptionPlansRequest,
  verifyProfileRequest,
  updateBankRequestInvoiceInfo,
  updateUserWatermarkInfoRequest,
} from './ProfileAPI'
import {ResponseGenerator} from '../../../shared/models/ResponseGenerator'
import {HTTP_CODE, USER_ROLE} from '../../../shared/constants'
import {IProfileDetails, ISubscriptionPlanModel} from '../models/ProfileModels'
import {requestUser} from '../../auth/redux/AuthRedux'
import {UpdateWatermarkPayload} from '../models/ProfileModels'
import {getCurrentIntl} from '../../../shared/utils/i18nUtils'
import {getPersonInChargePlan} from '../../auth/redux/AuthAPI'

const showSuccess = (message: string) => {
  toast.info(message, {autoClose: 3000, hideProgressBar: true})
}
export default function* saga() {
  yield takeLatest(verifyUser.type, function* verifyProfile() {
    try {
      const response: ResponseGenerator = yield call(verifyProfileRequest)
      //@TODO: this function may not use any more
      if (response && response.status === HTTP_CODE.SUCCESS) {
        const profileResponse: IProfileDetails = response.data
        yield put(setUserProfile({profile: profileResponse}))

        const currentIntl = getCurrentIntl()
        toast.success(currentIntl.formatMessage({id: 'PROFILE.VERIFY.ACCEPT'}))
      } else {
        // TODO: i18n
        throw new Error('Can not get user profile. Please try again later.')
      }
    } catch (error: any) {
      yield put(showError({error: error}))
    }
  })
  yield takeLatest(getUserProfile.type, function* getProfile() {
    try {
      const response: ResponseGenerator = yield call(getUserProfileRequest)

      if (response && response.status === HTTP_CODE.SUCCESS) {
        const profileResponse: IProfileDetails = response.data
        if (profileResponse.role === USER_ROLE.BRANCH_EMPLOYEE && profileResponse.branchId) {
          const personInCharge: ResponseGenerator = yield call(getPersonInChargePlan)
          if (personInCharge && personInCharge.status === HTTP_CODE.SUCCESS) {
            yield put(
              setUserProfile({
                profile: {
                  ...profileResponse,
                  points: personInCharge.data.points,
                  planId: personInCharge.data.planId,
                  plan: personInCharge.data.plan,
                  userRate: personInCharge.data.userRate,
                },
              })
            )
          } else {
            throw new Error('SERVER.PLAN.NOT_FOUND')
          }
        } else {
          yield put(setUserProfile({profile: profileResponse}))
        }
      } else {
        // TODO: i18n
        throw new Error('Can not get user profile. Please try again later.')
      }
    } catch (error: any) {
      yield put(showError({error: error}))
    }
  })
  yield takeLatest(
    redeemReferralCode.type,
    function* redeemCode(action: ReturnType<typeof redeemReferralCode>) {
      try {
        const referenceCode = action.payload.referralCode
        const response: ResponseGenerator = yield call(redeemReferralCodeRequest, referenceCode)

        if (response && response.status === HTTP_CODE.SUCCESS) {
          const data = response.data
          yield put(redeemReferralCodeSuccess({points: data.points, referralCode: referenceCode}))
          yield put(requestUser())

          const currentIntl = getCurrentIntl()
          showSuccess(currentIntl.formatMessage({id: 'Referral.update_referral_successfully'}))
        } else {
          // TODO: i18n
          throw new Error('Can not redeem referral code. Please try again later.')
        }
      } catch (error: any) {
        yield put(showError({error: error}))
      }
    }
  )
  yield takeLatest(
    updateUserProfile.type,
    function* updateProfile(action: ReturnType<typeof updateUserProfile>) {
      try {
        const response: ResponseGenerator = yield call(
          updateUserProfileRequest,
          action.payload.profile
        )

        if (response && response.status === HTTP_CODE.SUCCESS) {
          const data: IProfileDetails = response.data
          yield put(setUserProfile({profile: data}))
          yield put(requestUser())
          yield put(setShowProfileEditor({show: false}))
          // TODO: i18n with PROFILE.MESSAGE.UPDATE_PROFILE_SUCCESSFUL

          const currentIntl = getCurrentIntl()
          showSuccess(currentIntl.formatMessage({id: 'PROFILE.MESSAGE.UPDATE_PROFILE_SUCCESSFUL'}))
        } else {
          // TODO: i18n
          throw new Error('Can not update profile. Please try again later.')
        }
      } catch (error: any) {
        yield put(showError({error: error}))
      }
    }
  )
  yield takeLatest(
    changePassword.type,
    function* updatePassword(action: ReturnType<typeof changePassword>) {
      try {
        const response: ResponseGenerator = yield call(changePasswordRequest, action.payload)

        if (response && response.status === HTTP_CODE.SUCCESS) {
          const isSuccess: boolean = response.data.isSuccess

          if (isSuccess) {
            yield put(setShowPasswordChange({show: false}))
            yield put(changePasswordSuccess())
            // TODO: i18n with PROFILE.MESSAGE.UPDATE_PASSWORD_SUCCESSFUL
            showSuccess('Update password successfully')
          }
        } else {
          // TODO: i18n
          throw new Error('Can not change password. Please try again later.')
        }
      } catch (error: any) {
        yield put(showError({error: error}))
      }
    }
  )
  yield takeLatest(
    getSubscriptionPlans.type,
    function* getAllPlans(action: ReturnType<typeof getSubscriptionPlans>) {
      try {
        const response: ResponseGenerator = yield call(
          getSubscriptionPlansRequest,
          action.payload.type
        )

        if (response && response.status === HTTP_CODE.SUCCESS) {
          const data: ISubscriptionPlanModel[] = response.data

          if (data.length > 0) {
            yield put(setSubscriptionPlans({subscriptionPlans: data}))
          }
        } else {
          // TODO: i18n
          throw new Error('Can not change password. Please try again later.')
        }
      } catch (error: any) {
        yield put(showError({error: error}))
      }
    }
  )
  yield takeLatest(
    updateUserInvoice.type,
    function* updateUserBankInvoice(action: ReturnType<typeof updateUserInvoice>) {
      const invoiceInfo = action.payload.data
      const isAutoSubmitRequest = action.payload.autoSubmit
      try {
        const response: ResponseGenerator = yield call(updateBankRequestInvoiceInfo, invoiceInfo)
        if (response && response.status === HTTP_CODE.SUCCESS) {
          if (isAutoSubmitRequest) {
            const response: ResponseGenerator = yield call(verifyProfileRequest)

            if (response && response.status === HTTP_CODE.SUCCESS) {
              const profileResponse: IProfileDetails = response.data
              yield put(setUserProfile({profile: profileResponse}))
              yield put(setBankTransferRequestModal(false))

              const currentIntl = getCurrentIntl()
              toast.success(currentIntl.formatMessage({id: 'PROFILE.VERIFY.ACCEPT'}))
            } else {
              throw new Error('PROFILE.MESSAGE.SUBMIT_REQUEST_FAILED')
            }
          } else {
            const data: IProfileDetails = response.data
            yield put(setUserProfile({profile: data}))
            yield put(setShowInvoiceEditor(false))

            const currentIntl = getCurrentIntl()
            showSuccess(
              currentIntl.formatMessage({id: 'PROFILE.MESSAGE.UPDATE_INVOICE_SUCCESSFUL'})
            )
          }
        } else {
          throw new Error('PROFILE.MESSAGE.UPDATE_INVOICE_FAILED')
        }
      } catch (error: any) {
        yield put(showError({error: error}))
      }
    }
  )

  yield takeLatest(
    updateUserWatermarkInfo.type,
    function* updateWatermark(action: ReturnType<typeof updateUserWatermarkInfo>) {
      try {
        const payload: UpdateWatermarkPayload = action.payload.watermarkInfo
        const response: ResponseGenerator = yield call(updateUserWatermarkInfoRequest, payload)
        if (response && response.status === HTTP_CODE.SUCCESS) {
          yield put(requestUser())
          const currentIntl = getCurrentIntl()
          showSuccess(currentIntl.formatMessage({id: 'PROFILE.WATERMARK.UPDATE_SUCCESS'}))
        } else {
          throw new Error('PROFILE.WATERMARK.UPDATE_FAILED')
        }
      } catch (error: any) {
        yield put(showError({error: error}))
      }
    }
  )
}
